import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import ActivityCalendar, { HexCell, intensityClassFor } from './ActivityCalendar'
import { getCheckins, Checkin, CheckinsListResponse } from '../api/client'
import { toLocalDateStr } from '../utils/dates'

interface ProjectActivityCalendarProps {
  projectId: number
  className?: string
  onLifetimeEmptyChange?: (empty: boolean) => void
}

const ProjectActivityCalendar: React.FC<ProjectActivityCalendarProps> = ({
  projectId,
  className = '',
  onLifetimeEmptyChange
}) => {
  const [checkins, setCheckins] = useState<Checkin[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // State for current displayed range - show current month and up to 2 past months
  const currentDate = new Date()
  const [endYear, setEndYear] = useState(currentDate.getFullYear())
  const [endMonth, setEndMonth] = useState(currentDate.getMonth()) // 0-11
  const monthsToShow = 3 // Maximum number of months to display

  useEffect(() => {
    const fetchProjectCheckins = async () => {
      setLoading(true)
      setError(null)

      try {
        // Calculate the range - show current month and up to 2 months before
        const startMonth = endMonth - (monthsToShow - 1)
        const startYear = startMonth < 0 ? endYear - 1 : endYear
        const adjustedStartMonth = startMonth < 0 ? startMonth + 12 : startMonth

        // Get checkins for the displayed months range
        const startDate = toLocalDateStr(new Date(startYear, adjustedStartMonth, 1))
        const endDate = toLocalDateStr(new Date(endYear, endMonth + 1, 0))

        const response: CheckinsListResponse = await getCheckins(
          undefined, // featureId
          projectId,
          1000, // limit - high number to get all checkins in the range
          undefined, // textSearch
          'most_recent', // sortOrder
          startDate,
          endDate
        )

        setCheckins(response.checkins)
      } catch (err) {
        console.error('Error fetching project checkins:', err)
        setError('Failed to load activity data')
      } finally {
        setLoading(false)
      }
    }

    fetchProjectCheckins()
  }, [projectId, endYear, endMonth, monthsToShow])

  // Lifetime probe: is there any checkin on this project, ever? The
  // month-window fetch above can't answer that (a quiet recent quarter
  // would look empty even on a project with old activity), so we fire a
  // separate unbounded limit-1 query whose only job is to drive the
  // section's lifetime-empty signal upward.
  useEffect(() => {
    let cancelled = false
    getCheckins(undefined, projectId, 1)
      .then((resp) => {
        if (cancelled) return
        onLifetimeEmptyChange?.(resp.checkins.length === 0)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Error probing project lifetime checkins:', err)
      })
    return () => {
      cancelled = true
    }
  }, [projectId, onLifetimeEmptyChange])

  // Process checkins into activity data (date -> count)
  const activityData = useMemo(() => {
    const data: Record<string, number> = {}

    checkins.forEach(checkin => {
      const date = checkin.applies_to.split('T')[0] // Get YYYY-MM-DD format
      data[date] = (data[date] || 0) + 1
    })

    return data
  }, [checkins])

  // Get maximum activity count for scaling colors
  const maxActivity = useMemo(() => {
    return Math.max(1, ...Object.values(activityData))
  }, [activityData])

  const renderCell = (date: string, activityCount: number) => {
    // Format date nicely for tooltip
    const dateObj = new Date(date + 'T00:00:00')
    const formattedDate = dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })

    return (
      <HexCell
        colorClass={intensityClassFor(activityCount, maxActivity)}
        title={`${formattedDate}: ${activityCount} checkin${activityCount === 1 ? '' : 's'}`}
      />
    )
  }

  if (error) {
    return (
      <div className={className}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">Activity</h2>
        </div>
        <div className="text-rose-500 text-xs">{error}</div>
      </div>
    )
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const totalCheckins = Object.values(activityData).reduce((sum, count) => sum + count, 0)

  // Navigation functions - navigate by moving the end month
  const goToPreviousRange = () => {
    let newMonth = endMonth - monthsToShow
    let newYear = endYear

    while (newMonth < 0) {
      newMonth += 12
      newYear -= 1
    }

    setEndMonth(newMonth)
    setEndYear(newYear)
  }

  const goToNextRange = () => {
    let newMonth = endMonth + monthsToShow
    let newYear = endYear

    while (newMonth > 11) {
      newMonth -= 12
      newYear += 1
    }

    // Don't go beyond current month
    const today = new Date()
    if (newYear > today.getFullYear() || (newYear === today.getFullYear() && newMonth > today.getMonth())) {
      setEndYear(today.getFullYear())
      setEndMonth(today.getMonth())
    } else {
      setEndMonth(newMonth)
      setEndYear(newYear)
    }
  }

  const goToCurrentMonth = () => {
    const today = new Date()
    setEndYear(today.getFullYear())
    setEndMonth(today.getMonth())
  }

  const isCurrentPeriod = endYear === currentDate.getFullYear() && endMonth === currentDate.getMonth()

  // Generate array of months to display (current month and up to 2 past months)
  const getMonthsToDisplay = () => {
    const months = []
    for (let i = monthsToShow - 1; i >= 0; i--) {
      let month = endMonth - i
      let year = endYear

      if (month < 0) {
        month += 12
        year -= 1
      }

      months.push({ month, year })
    }
    return months
  }

  const monthsToDisplay = getMonthsToDisplay()

  // On narrow viewports the 3-month strip overflows its container; pin the
  // initial scroll position to the right (most-recent) edge instead of the
  // default left edge. Re-runs on every paging change so the new range also
  // starts pinned right.
  const stripRef = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    const el = stripRef.current
    if (!el) return
    el.scrollLeft = el.scrollWidth
  }, [endYear, endMonth, monthsToShow])

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold text-gray-800">Activity</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousRange}
              className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
              title="Previous months"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goToNextRange}
              className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
              title="Next months"
              disabled={isCurrentPeriod}
            >
              <svg className={`w-4 h-4 ${isCurrentPeriod ? 'text-gray-300' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {!isCurrentPeriod && (
              <button
                onClick={goToCurrentMonth}
                className="text-sm text-amber-800 hover:text-amber-900 underline ml-2"
                title="Go to current month"
              >
                Today
              </button>
            )}
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {totalCheckins} checkin{totalCheckins === 1 ? '' : 's'}
        </div>
      </div>

      {/* Multiple calendars side by side. Always rendered (no skeleton swap)
          so paging arrows don't reshape the section while data is in flight —
          cells just fade between data states. */}
      <div
        ref={stripRef}
        className={`flex gap-4 overflow-x-auto transition-opacity ${loading ? 'opacity-60' : ''}`}
      >
        {monthsToDisplay.map(({ month, year }) => (
          <div key={`${year}-${month}`} className="flex-shrink-0">
            <div className="text-[10px] font-medium text-gray-500 mb-1 text-center">
              {monthNames[month].slice(0, 3)}
            </div>
            <ActivityCalendar
              year={year}
              month={month}
              activityData={activityData}
              renderCell={renderCell}
              className=""
            />
          </div>
        ))}
      </div>

    </div>
  )
}

export default ProjectActivityCalendar